import { useEffect, useId, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { TreeSelectPicker } from './TreeSelectPicker';
import { useTreeLookupNodesFromUrl } from '@/lib/treeLookup';

/**
 * Standalone composite picker for a PrincipalRef ({type, id, qualifier}). Shared by the
 * JSONForms PrincipalRenderer and the custom OrgUnit tree detail panel so both behave
 * identically. Resolves an already-stored principal id to its display name (not the raw
 * GUID) via the per-type by-id endpoints. spec:PrincipalrefOwnership §3/§12.
 */

export interface PrincipalValue {
  type?: string;
  id?: string;
  qualifier?: string;
}

export interface PrincipalEndpoints {
  user: string;    // user search endpoint (.../search)
  role: string;    // role search endpoint (.../search)
  orgUnit: string; // org-unit list endpoint (ABP paged); by-id = endpoint + '/{id}'
  qualifier: { endpoint: string; valueField: string; displayField: string };
}

export const DEFAULT_PRINCIPAL_ENDPOINTS: PrincipalEndpoints = {
  user: '/api/identity/user-picker/search',
  role: '/api/identity/role-picker/search',
  orgUnit: '/api/core/orgunit',
  qualifier: { endpoint: '/api/core/orgrolefunction', valueField: 'code', displayField: 'name' },
};

export const DEFAULT_PRINCIPAL_TYPES = [
  { code: 'USER', name: 'User' },
  { code: 'ROLE', name: 'Role' },
  { code: 'ORG_UNIT', name: 'Org Unit' },
  { code: 'ORG_ROLE', name: 'Org Role' },
];

interface Option { id: string; label: string }

interface Props {
  value: PrincipalValue;
  onChange: (next: PrincipalValue) => void;
  endpoints?: PrincipalEndpoints;
  typeOptions?: { code: string; name: string }[];
  disabled?: boolean;
  required?: boolean;
  label?: string;
  error?: string;
  /** Pre-fill the org-node for OrgRole/OrgUnit (e.g. the current OrgUnit, so "the
   *  Controller of THIS unit" needs only the Function picked). */
  defaultNodeId?: string;
}

function toArray(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

const userLabel = (o: any) => (o?.name ? `${o.name}${o.surname ? ' ' + o.surname : ''}` : o?.userName) || '';
const orgUnitLabel = (o: any) => (o?.code ? `${o.name} (${o.code})` : o?.name) || '';

/**
 * Resolve a stored PrincipalRef ({type, id}) to its display option ({id, label}) via the per-type
 * by-id endpoints, so a raw GUID is never shown. Returns null when nothing is selected, and an
 * optimistic {id, label:id} fallback while the name loads. Shared by the PrincipalPicker itself and
 * by read-only surfaces that need to show an assigned principal as plain text.
 */
export function usePrincipalLabel(
  value: PrincipalValue,
  endpoints: PrincipalEndpoints = DEFAULT_PRINCIPAL_ENDPOINTS,
): { id: string; label: string } | null {
  const type = value.type || '';
  const id = value.id;
  const [option, setOption] = useState<{ id: string; label: string } | null>(null);

  useEffect(() => {
    if (!id || !type) { setOption(null); return; }
    let cancelled = false;
    const byId = (() => {
      switch (type) {
        case 'USER': return { url: `${endpoints.user.replace(/\/search$/, '')}/by-id/${id}`, labelOf: userLabel };
        case 'ROLE': return { url: `${endpoints.role.replace(/\/search$/, '')}/by-id/${id}`, labelOf: (o: any) => o?.name || '' };
        case 'ORG_UNIT':
        case 'ORG_ROLE': return { url: `${endpoints.orgUnit}/${id}`, labelOf: orgUnitLabel };
        default: return null;
      }
    })();
    if (!byId) { setOption({ id, label: id }); return; }
    setOption({ id, label: id }); // optimistic fallback while resolving
    apiClient.get(byId.url)
      .then(res => { if (!cancelled) setOption({ id, label: byId.labelOf(res.data) || id }); })
      .catch(() => { /* keep id fallback */ });
    return () => { cancelled = true; };
  }, [type, id, endpoints]);

  return option;
}

export function PrincipalPicker({
  value, onChange, endpoints = DEFAULT_PRINCIPAL_ENDPOINTS,
  typeOptions = DEFAULT_PRINCIPAL_TYPES, disabled, required, label, error, defaultNodeId,
}: Props) {
  const { t } = useTranslation();
  const type = value.type || '';
  // MUI renders <Select> as a `div[role="combobox"]`; a bare <InputLabel> names it only through
  // `labelId` (→ aria-labelledby). Same binding as StaticEnumRenderer/EnumRenderer.
  const typeLabelId = useId();
  const qualifierLabelId = useId();

  const [options, setOptions] = useState<Option[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // Resolve the already-selected id to its display name (so we never show a raw GUID).
  const currentOption = usePrincipalLabel(value, endpoints);
  const [qualifierOptions, setQualifierOptions] = useState<Option[]>([]);
  // How many active members the org-based principal currently resolves to (null = N/A).
  const [resolvedCount, setResolvedCount] = useState<number | null>(null);

  // Org-node types render the expandable org tree instead of the flat autocomplete;
  // the whole unit list is loaded once and filtered client-side by the tree picker.
  const isOrgNodeType = type === 'ORG_UNIT' || type === 'ORG_ROLE';
  const orgNodes = useTreeLookupNodesFromUrl(isOrgNodeType ? endpoints.orgUnit : null);

  // Per-type search config (URL, search param, label) — USER/ROLE only.
  const searchCfg = useMemo(() => {
    switch (type) {
      case 'USER': return { url: endpoints.user, param: 'filter', labelOf: userLabel };
      case 'ROLE': return { url: endpoints.role, param: 'filter', labelOf: (o: any) => o?.name || '' };
      default: return null;
    }
  }, [type, endpoints]);

  // Search options as the user types.
  useEffect(() => {
    if (!searchCfg) { setOptions([]); return; }
    let cancelled = false;
    setLoading(true);
    // Empty input returns the first 25 (default page shown on open); typing narrows it server-side.
    const params: any = { maxResultCount: 25 };
    if (input) params[searchCfg.param] = input;
    const timer = setTimeout(() => {
      apiClient.get(searchCfg.url, { params })
        .then(res => {
          if (cancelled) return;
          setOptions(toArray(res.data).map((o: any) => ({ id: o.id, label: searchCfg.labelOf(o) || o.id })));
        })
        .catch(() => { if (!cancelled) setOptions([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchCfg, input]);

  // OrgRoleFunction options for the qualifier dropdown.
  useEffect(() => {
    if (type !== 'ORG_ROLE') return;
    let cancelled = false;
    apiClient.get(endpoints.qualifier.endpoint, { params: { maxResultCount: 100 } })
      .then(res => {
        if (cancelled) return;
        setQualifierOptions(toArray(res.data).map((o: any) => ({
          id: o[endpoints.qualifier.valueField],
          label: o[endpoints.qualifier.displayField] || o[endpoints.qualifier.valueField],
        })));
      })
      .catch(() => { if (!cancelled) setQualifierOptions([]); });
    return () => { cancelled = true; };
  }, [type, endpoints]);

  // Preview how many active members an OrgRole/OrgUnit principal resolves to right now,
  // so the admin knows e.g. "Controller of EMEA" currently maps to nobody. The principal
  // legitimately resolves to an empty set ("unassigned") — this is a heads-up, not a block.
  useEffect(() => {
    let url: string | null = null;
    const params: any = {};
    const base = '/api/core/orgunitmember';
    if (type === 'ORG_ROLE' && value.id && value.qualifier) {
      url = `${base}/by-orgunit-and-role`; params.orgUnitId = value.id; params.role = value.qualifier;
    } else if (type === 'ORG_UNIT' && value.id) {
      url = `${base}/by-orgunit/${value.id}`;
    }
    if (!url) { setResolvedCount(null); return; }
    let cancelled = false;
    apiClient.get(url, { params })
      .then(res => { if (!cancelled) setResolvedCount(toArray(res.data).length); })
      .catch(() => { if (!cancelled) setResolvedCount(null); });
    return () => { cancelled = true; };
  }, [type, value.id, value.qualifier]);

  const showIdPicker = type === 'USER' || type === 'ROLE';
  const showQualifier = type === 'ORG_ROLE';
  // Prepend the resolved selection so it is shown while its page is not loaded — but not while the
  // user is typing, where it would sit in the result list without matching the query.
  const idOptions = currentOption && !input && !options.some(o => o.id === currentOption.id)
    ? [currentOption, ...options] : options;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {label && <Typography variant="caption" color="text.secondary">{label}</Typography>}
      {/* The inner controls wrap, and their preferred widths yield to the cell they were given —
          a form column narrows well below them once a drawer opens beside the form. */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <FormControl size="small" sx={{ minWidth: 'min(150px, 100%)' }} required={required} error={!!error} disabled={disabled}>
          <InputLabel id={typeLabelId}>{t('principal.type', { defaultValue: 'Type' })}</InputLabel>
          <Select
            labelId={typeLabelId}
            value={type}
            label={t('principal.type', { defaultValue: 'Type' })}
            displayEmpty={!required}
            onChange={(e) => {
              const newType = (e.target.value as string) || undefined;
              // Pre-fill the node for OrgRole/OrgUnit (usually "this" org unit) so the user
              // typically only picks the Function.
              const prefilledId = (newType === 'ORG_ROLE' || newType === 'ORG_UNIT') ? defaultNodeId : undefined;
              onChange({ type: newType, id: prefilledId, qualifier: undefined });
            }}
          >
            {!required && <MenuItem value=""><em>{t('common.none', { defaultValue: 'None' })}</em></MenuItem>}
            {typeOptions.map(o => (
              <MenuItem key={o.code} value={o.code}>
                {t(`Enum:PrincipalType:${o.code}`, { defaultValue: o.name })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* OrgRole reads "Function … of Org Unit": Function first, then the node. */}
        {showQualifier && (
          <FormControl size="small" sx={{ minWidth: 'min(170px, 100%)' }} disabled={disabled} required={required} error={!!error}>
            <InputLabel id={qualifierLabelId}>{t('orgRoleFunction.function', { defaultValue: 'Function' })}</InputLabel>
            <Select
              labelId={qualifierLabelId}
              value={value.qualifier || ''}
              label={t('orgRoleFunction.function', { defaultValue: 'Function' })}
              onChange={(e) => onChange({ ...value, qualifier: (e.target.value as string) || undefined })}
            >
              {qualifierOptions.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {isOrgNodeType && (
          <Box sx={{ flex: 1, minWidth: 'min(220px, 100%)' }}>
            <TreeSelectPicker
              nodes={orgNodes}
              value={value.id || null}
              onChange={(nodeId) => onChange({ ...value, id: nodeId ?? undefined })}
              label={type === 'ORG_ROLE'
                ? t('principal.ofOrgUnit', { defaultValue: 'of Org Unit' })
                : t('orgUnit.title', { defaultValue: 'Org Unit' })}
              disabled={disabled}
              size="small"
              variant="standard"
              popupMinWidth={360}
            />
          </Box>
        )}
        {showIdPicker && (
          <Autocomplete
            sx={{ flex: 1, minWidth: 'min(220px, 100%)' }}
            options={idOptions}
            getOptionLabel={(o: Option) => o?.label ?? ''}
            value={currentOption}
            loading={loading}
            disabled={disabled}
            onInputChange={(_e, val, reason) => setInput(reason === 'reset' ? '' : val)}
            filterOptions={(x) => x}
            openOnFocus
            onChange={(_e, val: Option | null) => onChange({ ...value, id: val ? val.id : undefined })}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            renderInput={(p) => (
              <TextField
                {...p}
                label={type === 'ROLE' ? t('common.role', { defaultValue: 'Role' })
                  : t('common.user', { defaultValue: 'User' })}
                size="small"
                variant="standard"
                InputProps={{ ...p.InputProps, endAdornment: (<>{loading ? <CircularProgress size={16} /> : null}{p.InputProps.endAdornment}</>) }}
              />
            )}
          />
        )}
      </Box>
      {resolvedCount !== null && (
        <Typography variant="caption" color={resolvedCount === 0 ? 'warning.main' : 'text.secondary'}>
          {resolvedCount === 0
            ? t('principal.resolvesNone', { defaultValue: 'Resolves to 0 active members — owner will be unassigned until someone holds this function/node.' })
            : t('principal.resolvesN', { count: resolvedCount, defaultValue: `Resolves to ${resolvedCount} user(s)` })}
        </Typography>
      )}
      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </Box>
  );
}
